import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, Container, Grid, useTheme, useMediaQuery, InputAdornment, TextField, IconButton, Card, CardContent } from '@mui/material';
import { Icon } from '@iconify/react';
import TopBar from '../components/TopBar';
import Sidebar from '../components/Sidebar';
import UserUploadCard from '../components/UserUploadCard';
import RecordsTable from '../components/RecordsTable';
import UserFormDrawer from '../components/UserFormDrawer';
import AuditLogDrawer from '../components/AuditLogDrawer';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import PasswordResetHistoryDrawer from '../components/PasswordResetHistoryDrawer';
import { useToast } from '../components/ui/ToastProvider';
import { logUserCreation, logUserEdit, logStatusChange, simulateBankAPISync, logPasswordReset, logEmailResend } from '../utils/auditLog';
import { generateSecureOTP, hashPassword } from '../utils/passwordPolicy';
import { sendNewUserOTPEmail, sendPasswordResetEmail } from '../utils/emailService';
import { createPasswordResetHistory } from '../utils/passwordResetHistory';
import { shouldShowResendEmail } from '../utils/passwordResetHistory';
import RecordUploadCard from '../components/RecordUploadCard';
import { Record } from '../types/Record';
interface BranchUsersProps {
  onLogout: () => void;
}
const STORAGE_KEY = 'branch_records';
// Mock data for initial load if no data in localStorage
const INITIAL_MOCK_RECORDS = Array(25).fill(null).map((_, index) => ({
  id: index + 1,
  fullName: ['John Doe', 'Jane Smith', 'Ahmed Khan', 'Sara Ali', 'Bilal Ahmad'][index % 5],
  username: ['john.doe', 'jane.smith', 'ahmed.khan', 'sara.ali', 'bilal.ahmad'][index % 5],
  email: `user${index + 1}@example.com`,
  mobile: `+92 300 ${1000000 + index}`,
  branchCode: ['0001', '0002', '0003', '0004', '0005', '0006', '0007', '0008'][index % 8],
  settlementAccount: `ACC-${10000000 + index}`,
  designation: ['Manager', 'Officer', 'Assistant', 'Clerk'][index % 4],
  agentCode: ['Bank of AJ&K', 'BIPL', 'FBL', 'HBL', 'UBL', 'MCB', 'ABL'][index % 7],
  status: index % 4 === 0 ? 'inactive' : 'active',
  createdAt: new Date(Date.now() - Math.floor(Math.random() * 10000000000)).toISOString()
}));
export default function BranchUsers({
  onLogout
}: BranchUsersProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showUploadCard, setShowUploadCard] = useState(false);
  const [userDrawerOpen, setUserDrawerOpen] = useState(false);
  const [auditLogDrawerOpen, setAuditLogDrawerOpen] = useState(false);
  const [editUser, setEditUser] = useState<any>(null);
  const [selectedUserForAudit, setSelectedUserForAudit] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [records, setRecords] = useState<any[]>([]);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [selectedUserForReset, setSelectedUserForReset] = useState<any>(null);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [resetPasswordError, setResetPasswordError] = useState<string | undefined>();
  const [resetHistoryDrawerOpen, setResetHistoryDrawerOpen] = useState(false);
  const [selectedUserForResetHistory, setSelectedUserForResetHistory] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const {
    showToast
  } = useToast();
  // Load records from localStorage on mount
  useEffect(() => {
    const savedRecords = localStorage.getItem(STORAGE_KEY);
    if (savedRecords) {
      try {
        const parsedRecords = JSON.parse(savedRecords);
        setRecords(parsedRecords);
      } catch (error) {
        console.error('Error loading records from localStorage:', error);
        setRecords(INITIAL_MOCK_RECORDS);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(INITIAL_MOCK_RECORDS));
      }
    } else {
      // First time load - use mock data
      setRecords(INITIAL_MOCK_RECORDS);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(INITIAL_MOCK_RECORDS));
    }
  }, []);
  // Save records to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(records));
    } catch (error) {
      console.error('Error saving records to localStorage:', error);
      showToast('Failed to save record data', 'error');
    }
  }, [records]);
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  const handleDownloadSample = () => {
    console.log('Download sample Excel');
    showToast('Sample template downloaded', 'success');
  };
  const handleUploadUsers = () => {
    setShowUploadCard(true);
  };
  const handleAddUser = () => {
    setEditUser(null);
    setUserDrawerOpen(true);
  };
  const handleEditUser = (user: any) => {
    setEditUser(user);
    setUserDrawerOpen(true);
  };
  const handleViewAuditLog = (user: any) => {
    setSelectedUserForAudit({
      id: user.id.toString(),
      name: user.fullName
    });
    setAuditLogDrawerOpen(true);
  };
  const handleCloseUserDrawer = () => {
    setUserDrawerOpen(false);
    setEditUser(null);
  };
  const handleCloseAuditLogDrawer = () => {
    setAuditLogDrawerOpen(false);
    setSelectedUserForAudit(null);
  };
  const handleSaveUser = async (userData: any) => {
    if (!userData.id) {
      // New record creation
      const newOTP = generateSecureOTP();
      const hashedOTP = hashPassword(newOTP);
      const newRecord = {
        ...userData,
        id: records.length > 0 ? Math.max(...records.map(r => r.id)) + 1 : 1,
        status: 'active' as const,
        createdAt: new Date().toISOString(),
        agentCode: 'BIPL',
        hashedPassword: hashedOTP,
        firstLoginRequired: true,
        passwordResetRequired: true
      };
      // Add to records list
      const updatedRecords = [newRecord, ...records];
      setRecords(updatedRecords);
      // Log record creation in audit log
      logUserCreation(newRecord.id.toString(), newRecord.fullName, newRecord);
      // Send OTP email
      try {
        showToast('Sending OTP email...', 'info', 'Creating Record', 2000);
        const emailResult = await sendNewUserOTPEmail({
          recipientEmail: newRecord.email,
          recipientName: newRecord.fullName,
          otp: newOTP,
          expiryMinutes: 24
        });
        // Create password reset history entry
        createPasswordResetHistory(newRecord.id.toString(), newRecord.fullName, 'SYSTEM', newRecord.email, emailResult.success ? 'SUCCESS' : 'FAILED', {
          resetByName: 'System (Record Creation)',
          reason: 'New record created',
          deliveryError: emailResult.error,
          emailMessageId: emailResult.messageId
        });
        if (emailResult.success) {
          setUserDrawerOpen(false);
          showToast(`Record created successfully. Temporary password sent to ${newRecord.email}`, 'success', 'Record Created', 6000);
        } else {
          setUserDrawerOpen(false);
          showToast(`Record created but email delivery failed: ${emailResult.error}. Temporary password: ${newOTP}`, 'error', 'Email Delivery Failed', 10000);
        }
      } catch (error) {
        // Log email failure
        createPasswordResetHistory(newRecord.id.toString(), newRecord.fullName, 'SYSTEM', newRecord.email, 'FAILED', {
          resetByName: 'System (Record Creation)',
          reason: 'New record created',
          deliveryError: 'Email service error'
        });
        setUserDrawerOpen(false);
        showToast(`Record created but email failed to send. Temporary password: ${newOTP}`, 'error', 'Email Service Error', 10000);
      }
    } else {
      // Record update
      const oldRecord = records.find(r => r.id === userData.id);
      const updatedRecord = {
        ...userData,
        status: userData.status || 'active',
        createdAt: oldRecord?.createdAt || new Date().toISOString(),
        agentCode: oldRecord?.agentCode || 'BIPL'
      };
      // Update records list
      const updatedRecords = records.map(r => r.id === userData.id ? updatedRecord : r);
      setRecords(updatedRecords);
      // Log record edit in audit log immediately
      if (oldRecord) {
        logUserEdit(userData.id.toString(), userData.fullName, oldRecord, updatedRecord);
      }
      setUserDrawerOpen(false);
      showToast('Record updated successfully', 'success');
    }
  };
  const handleStatusChange = (record: any, newStatus: 'active' | 'inactive') => {
    // Update record status
    const updatedRecords = records.map(r => r.id === record.id ? {
      ...r,
      status: newStatus
    } : r);
    setRecords(updatedRecords);
    // Log status change immediately
    logStatusChange(record.id.toString(), record.fullName, record.status, newStatus);
    showToast(`Record ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`, 'success');
  };
  const handleResetPassword = (user: any) => {
    setSelectedUserForReset(user);
    setResetPasswordDialogOpen(true);
    setResetPasswordError(undefined);
  };
  const confirmResetPassword = async (reason?: string) => {
    if (!selectedUserForReset) return;
    setIsResettingPassword(true);
    setResetPasswordError(undefined);
    try {
      // Generate new secure OTP
      const newOTP = generateSecureOTP();
      const hashedOTP = hashPassword(newOTP);
      // Update record with new password and reset flag
      const updatedRecords = records.map(r => r.id === selectedUserForReset.id ? {
        ...r,
        hashedPassword: hashedOTP,
        passwordResetRequired: true,
        lastPasswordReset: new Date().toISOString()
      } : r);
      setRecords(updatedRecords);
      // Log password reset in audit log
      logPasswordReset(selectedUserForReset.id.toString(), selectedUserForReset.fullName, reason);
      // Send password reset email
      const emailResult = await sendPasswordResetEmail({
        recipientEmail: selectedUserForReset.email,
        recipientName: selectedUserForReset.fullName,
        otp: newOTP,
        resetBy: 'BO Admin',
        reason: reason
      });
      // Create password reset history entry
      createPasswordResetHistory(selectedUserForReset.id.toString(), selectedUserForReset.fullName, 'BO_ADMIN', selectedUserForReset.email, emailResult.success ? 'SUCCESS' : 'FAILED', {
        resetByName: 'Admin User',
        reason: reason || 'Password reset requested by admin',
        deliveryError: emailResult.error,
        emailMessageId: emailResult.messageId
      });
      // Close dialog
      setResetPasswordDialogOpen(false);
      setIsResettingPassword(false);
      setSelectedUserForReset(null);
      // Show appropriate toast based on email delivery
      if (emailResult.success) {
        showToast(`Password reset successful. New temporary password sent to ${selectedUserForReset.email}`, 'success', 'Password Reset', 6000);
      } else {
        showToast(`Password reset successful but email delivery failed: ${emailResult.error}. New temporary password: ${newOTP}`, 'warning', 'Password Reset - Email Failed', 10000);
      }
    } catch (error) {
      setResetPasswordError('Failed to reset password. Please try again.');
      setIsResettingPassword(false);
    }
  };
  const handleSyncFromBank = (record: any) => {
    // Simulate syncing data from Bank API
    const syncedData = {
      branchCode: ['0001', '0002', '0003', '0004', '0005'][Math.floor(Math.random() * 5)],
      settlementAccount: `ACC-SYNC-${Math.floor(Math.random() * 1000000)}`,
      designation: ['Manager', 'Senior Officer', 'Officer'][Math.floor(Math.random() * 3)]
    };
    // Update record with synced data
    const updatedRecords = records.map(r => r.id === record.id ? {
      ...r,
      ...syncedData
    } : r);
    setRecords(updatedRecords);
    // Log sync in audit log immediately
    simulateBankAPISync(record.id.toString(), record.fullName, syncedData, record);
    showToast(`Record data synced from Bank API successfully`, 'success');
  };
  const handleViewResetHistory = (user: any) => {
    setSelectedUserForResetHistory({
      id: user.id.toString(),
      name: user.fullName
    });
    setResetHistoryDrawerOpen(true);
  };
  const handleResendEmail = async (record: any) => {
    try {
      showToast('Sending OTP email...', 'info', 'Resending Email', 2000);
      // Generate new secure OTP
      const newOTP = generateSecureOTP();
      const hashedOTP = hashPassword(newOTP);
      // Update record with new hashed password
      const updatedRecords = records.map(r => r.id === record.id ? {
        ...r,
        hashedPassword: hashedOTP,
        passwordResetRequired: true,
        lastPasswordReset: new Date().toISOString()
      } : r);
      setRecords(updatedRecords);
      // Send OTP email
      const emailResult = await sendPasswordResetEmail({
        recipientEmail: record.email,
        recipientName: record.fullName,
        otp: newOTP,
        resetBy: 'BO Admin',
        reason: 'Email resend requested by admin'
      });
      // Create password reset history entry
      createPasswordResetHistory(record.id.toString(), record.fullName, 'BO_ADMIN', record.email, emailResult.success ? 'SUCCESS' : 'FAILED', {
        resetByName: 'Admin User',
        reason: 'Email resend - OTP regenerated',
        deliveryError: emailResult.error,
        emailMessageId: emailResult.messageId
      });
      // Log email resend in audit log
      logEmailResend(record.id.toString(), record.fullName, record.email, emailResult.success, 'Email resend requested by admin');
      // Show appropriate toast based on email delivery
      if (emailResult.success) {
        showToast(`Email sent successfully to ${record.email}`, 'success', 'Email Sent', 6000);
      } else {
        showToast(`Email sending failed: ${emailResult.error}. New temporary password: ${newOTP}`, 'error', 'Email Failed', 10000);
      }
    } catch (error) {
      showToast('Failed to resend email. Please try again.', 'error', 'Error', 6000);
    }
  };
  const handleCloseUploadCard = () => {
    setShowUploadCard(false);
  };
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };
  const clearSearch = () => {
    setSearchQuery('');
  };
  return <Box sx={{
    width: '100%',
    height: '100%',
    bgcolor: 'background.default'
  }} data-id="element-3104" data-mp="12yqyjmtbfxt9">
      <TopBar onMenuClick={handleDrawerToggle} onLogout={onLogout} data-id="element-3105" data-mp="2xaykiannt4cw" />
      <Sidebar open={mobileOpen} onClose={handleDrawerToggle} activePage="user-management" data-id="element-3106" data-mp="2x8ks69kd6691" />
      <Box component="main" sx={{
      flexGrow: 1,
      p: 3,
      width: {
        xs: '100%',
        sm: `calc(100% - ${240}px)`
      },
      ml: {
        xs: 0,
        sm: `${240}px`
      },
      mt: 8
    }} data-id="element-3107" data-mp="zeqg6qogtiw2">
        <Container maxWidth="xl" data-id="element-3108" data-mp="1dehz95tu9pgx">
          {/* Header */}
          <Grid container spacing={2} alignItems="center" sx={{
          mb: 3
        }} data-id="element-3109" data-mp="1df169rhyatg6">
            <Grid item xs={12} md={6} data-id="element-3110" data-mp="14ecvd5nut97i">
              <Box sx={{
              display: 'flex',
              alignItems: 'center'
            }} data-id="element-3111" data-mp="14dbjx02xekp9">
                <Icon icon="lucide:user-cog" width={24} height={24} color="#0EA5E9" data-id="element-3112" data-mp="3p3j02o55fawq" />
                <Typography variant="h5" component="h1" sx={{
                ml: 1.5,
                fontWeight: 600
              }} data-id="element-3113" data-mp="3u0snjy8z9chx">
                  User Management
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6} data-id="element-3114" data-mp="3qlhdogtpkqmg">
              <Box sx={{
              display: 'flex',
              gap: 2,
              flexWrap: 'wrap',
              justifyContent: {
                xs: 'flex-start',
                md: 'flex-end'
              }
            }} data-id="element-3115" data-mp="3qlr2i130orqk">
                <Button variant="contained" startIcon={<Icon icon="lucide:plus" width={18} height={18} data-id="element-3117" data-mp="37toufe4fyjrx" />} onClick={handleAddUser} data-id="element-3116" data-mp="3cn14mj8dio15">
                  Add Record
                </Button>
              </Box>
            </Grid>
          </Grid>
          {/* Upload Card */}
          {showUploadCard && <RecordUploadCard onClose={handleCloseUploadCard} data-id="element-3118" data-mp="1tnsub1zmys8k" />}
          {/* Records Table */}
          <RecordsTable records={records} onEditRecord={handleEditUser} onViewAuditLog={handleViewAuditLog} onStatusChange={handleStatusChange} onResetPassword={handleResetPassword} onSyncFromBank={handleSyncFromBank} onViewResetHistory={handleViewResetHistory} onResendEmail={handleResendEmail} data-id="element-3119" data-mp="1ymvrk09dju5z" />
          {/* User Form Drawer */}
          <UserFormDrawer open={userDrawerOpen} user={editUser} onClose={handleCloseUserDrawer} onSave={handleSaveUser} data-id="element-3120" data-mp="2hilv3rwygylz" />
          {/* Audit Log Drawer */}
          <AuditLogDrawer open={auditLogDrawerOpen} onClose={() => setAuditLogDrawerOpen(false)} userId={selectedUserForAudit?.id} userName={selectedUserForAudit?.name} data-id="element-3121" data-mp="2w9kz0pmuiyz8" />
          {/* Reset Password Confirmation Dialog */}
          {selectedUserForReset && <ConfirmDialog open={resetPasswordDialogOpen} title="Reset Password" message={`Are you sure you want to reset the password for ${selectedUserForReset.fullName}? A new temporary password will be generated and sent to ${selectedUserForReset.email}.`} confirmLabel="Reset Password" showReason={true} reasonLabel="Reason for reset (optional)" reasonPlaceholder="Enter reason for password reset..." onConfirm={confirmResetPassword} onCancel={() => {
          setResetPasswordDialogOpen(false);
          setSelectedUserForReset(null);
        }} isLoading={isResettingPassword} error={resetPasswordError} data-id="element-3122" data-mp="klptqsirlgta" />}
          {/* Password Reset History Drawer */}
          <PasswordResetHistoryDrawer open={resetHistoryDrawerOpen} onClose={() => setResetHistoryDrawerOpen(false)} userId={selectedUserForResetHistory?.id} userName={selectedUserForResetHistory?.name} data-id="element-3123" data-mp="1m9i659cqwx8z" />
        </Container>
      </Box>
    </Box>;
}